import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { PRODUCT_TYPES } from '../lib/utils';
import {
  FileText,
  Plus,
  TrendingUp,
  Users,
  Package,
  ArrowRight,
  Clock,
  Layers
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { canCreate } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCodes, setRecentCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, codesRes] = await Promise.all([
        axios.get(`${API_URL}/api/codes/stats`),
        axios.get(`${API_URL}/api/codes?page=1&page_size=5`)
      ]);
      setStats(statsRes.data);
      setRecentCodes(codesRes.data.items || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Dashboard
            </h1>
            <p className="text-slate-500 mt-1">
              Visão geral do sistema de codificação
            </p>
          </div>
          {canCreate() && (
            <Link to="/criar">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm btn-active" data-testid="create-code-btn">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Novo Código
              </Button>
            </Link>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-slate-200 shadow-none card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    Total de Códigos
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2 font-heading">
                    {stats?.total_codes || 0}
                  </p>
                </div>
                <div className="p-2 bg-blue-50 rounded-md">
                  <FileText className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    Últimos 7 dias
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2 font-heading">
                    {stats?.recent_codes || 0}
                  </p>
                </div>
                <div className="p-2 bg-green-50 rounded-md">
                  <TrendingUp className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    Usuários
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2 font-heading">
                    {stats?.total_users || 0}
                  </p>
                </div>
                <div className="p-2 bg-violet-50 rounded-md">
                  <Users className="w-5 h-5 text-violet-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    Categorias
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2 font-heading">
                    {stats?.by_type?.length || 0}
                  </p>
                </div>
                <div className="p-2 bg-amber-50 rounded-md">
                  <Layers className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Codes */}
          <Card className="lg:col-span-2 border-slate-200 shadow-none">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-lg font-bold">
                Códigos Recentes
              </CardTitle>
              <Link to="/catalogo">
                <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                  Ver todos
                  <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentCodes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
                  <p>Nenhum código cadastrado ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentCodes.map((code) => (
                    <div
                      key={code.id}
                      className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-blue-300 transition-colors"
                      data-testid={`recent-code-${code.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <code className="material-code text-sm bg-slate-100 px-3 py-1.5 rounded text-slate-900">
                          {code.code}
                        </code>
                        <div className="hidden sm:block">
                          <p className="text-sm font-medium text-slate-900 truncate max-w-[300px]">
                            {code.description}
                          </p>
                          <p className="text-xs text-slate-500">
                            {code.product_type_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3" strokeWidth={1.5} />
                        {new Date(code.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* By Type */}
          <Card className="border-slate-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg font-bold">
                Por Tipo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.by_type?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Layers className="w-12 h-12 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
                  <p>Sem dados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stats?.by_type?.slice(0, 6).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 truncate flex-1 mr-2">
                        {item.type}
                      </span>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700 font-mono">
                        {item.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
